import { useState } from 'react';
import {
  IconArrowRight,
  IconAttention,
  IconBlock,
  IconDownload,
  IconSelected,
  IconTrash,
  formatDate,
  Textarea,
  ButtonSecondary,
  ClickOutsideDetector,
  IconLoader,
  DateInput,
  DateInputType,
  InputText,
} from '@ui/src';
import {
  ApplicationDocumentStatus,
  NewApplicationStatus,
} from '../../../../hoc/new-application/types';
import DocumentPreviewImage from './parts/document-preview';
import DocumentComment from './parts/document-comment';
import { OrganizationType } from '../../../../hoc/common/types';
import { ApplicationDocumentTypes } from './types';

import cn from './styles.module.css';

const Document = ({
  document,
  type,
  documentFileIsLoading,
  onDownload,
  downloadInProgress,
  applicationStatus,
  imgLink,
  moveInProgress,
  setDeletePopupVisible,
  onChangeCategory,
  addCommentAction,
  commentInProgress,
  commentsVisible,
  clientType,
  updateAllowed,
  updateInProgress,
  onChangeDocumentExpDate,
  changeDateError,
  allowedCategories,
  documentName,
  setDocumentName,
  nameError,
  renameAvailable,
  regulatedCompanyScheme,
  cofWithDocuments,
}: ApplicationDocumentTypes) => {
  const [comment, setComment] = useState('');
  const [menuVisible, setMenuVisible] = useState(false);

  let statusIcon;

  if (document?.status === ApplicationDocumentStatus.ACCEPTED) {
    statusIcon = (
      <div className={cn.documentRowAccepted}>
        <IconSelected
          className={cn.documentRowStatusIcon}
          height={9}
          width={13}
        />
      </div>
    );
  } else if (document?.status === ApplicationDocumentStatus.EXPIRED) {
    statusIcon = (
      <div className={cn.documentRowExpired}>
        <IconAttention />
      </div>
    );
  } else if (document?.status === ApplicationDocumentStatus.REJECTED) {
    statusIcon = (
      <div className={cn.documentRowRejected}>
        <IconBlock />
      </div>
    );
  } else {
    statusIcon = <div className={cn.documentRowNew} />;
  }

  let imageContent;

  if (documentFileIsLoading || (!document && downloadInProgress)) {
    imageContent = <IconLoader />;
  } else {
    imageContent = (
      <a
        href={imgLink}
        target="_blank"
        rel="noreferrer"
        aria-label="Document link"
      >
        <DocumentPreviewImage className={cn.documentPreviewIcon} />
      </a>
    );

    if (document?.upload?.mimeType?.includes('image')) {
      imageContent = (
        <a href={imgLink} target="_blank" rel="noreferrer">
          <img
            src={imgLink}
            alt={document?.name}
            className={
              documentFileIsLoading ||
              (!document && downloadInProgress) ||
              !imgLink
                ? cn.imageHidden
                : ''
            }
          />
        </a>
      );
    }
  }

  return (
    <div className={cn.documentSidepanelContent}>
      <div className={cn.documentPreview}>{imageContent}</div>
      <div className={cn.documentSidepanelControls}>
        {applicationStatus !== NewApplicationStatus.ACCEPTED &&
          applicationStatus !== NewApplicationStatus.DECLINED &&
          (clientType !== OrganizationType.INDIVIDUAL ||
            (clientType === OrganizationType.INDIVIDUAL &&
              (regulatedCompanyScheme || cofWithDocuments) &&
              !document?.documentCategoryId?.includes('identification'))) && (
            <div className={cn.documentSidepanelControlButtonWrapper}>
              <ClickOutsideDetector setOptionsListVisible={setMenuVisible}>
                <>
                  <button
                    type="button"
                    className={cn.documentSidepanelControlButton}
                    onClick={() => setMenuVisible(!menuVisible)}
                    disabled={moveInProgress}
                  >
                    <div className={cn.documentSidepanelControlButtonIcon}>
                      <IconArrowRight className={cn.arrow} />
                    </div>
                    {moveInProgress ? 'Loading...' : 'Move to group'}
                  </button>
                  {menuVisible && (
                    <ul className={cn.selectedDocumentsMenu}>
                      <li className={cn.selectedDocumentsMenuTitle}>
                        Which group to move document to?
                      </li>
                      {allowedCategories
                        ?.filter(
                          (item) => item.id !== type || item?.category !== type,
                        )
                        ?.filter((item) => !item.id?.includes('identification'))
                        ?.map((item) => (
                          <li key={item.id}>
                            <button
                              type="button"
                              className={cn.selectedDocumentsMenuButton}
                              onClick={() => {
                                if (onChangeCategory) {
                                  onChangeCategory(item.id);
                                }

                                setMenuVisible(false);
                              }}
                            >
                              {item.name}
                              <div>
                                Move <IconArrowRight />
                              </div>
                            </button>
                          </li>
                        ))}
                    </ul>
                  )}
                </>
              </ClickOutsideDetector>
            </div>
          )}

        <div className={cn.documentSidepanelControlButtonWrapper}>
          <button
            type="button"
            className={cn.documentSidepanelControlButton}
            onClick={onDownload}
            disabled={!imgLink}
          >
            <div className={cn.documentSidepanelControlButtonIcon}>
              <IconDownload />
            </div>
            Dowload
          </button>
        </div>
        {(clientType === OrganizationType.CORPORATE ||
          (clientType === OrganizationType.INDIVIDUAL &&
            (regulatedCompanyScheme || cofWithDocuments) &&
            !document?.documentCategoryId?.includes('identification'))) && (
          <div className={cn.documentSidepanelControlButtonWrapper}>
            <button
              type="button"
              className={cn.documentSidepanelControlButton}
              onClick={() =>
                setDeletePopupVisible && setDeletePopupVisible(true)
              }
            >
              <div className={cn.documentSidepanelControlButtonIcon}>
                <IconTrash />
              </div>
              Delete
            </button>
          </div>
        )}
      </div>
      <div className={cn.documentSidepanelInfo}>
        <div className={cn.documentSidepanelInfoStatus}>
          {document?.status} {statusIcon}
        </div>
        <div className={cn.documentSidepanelDate}>
          <div className={cn.documentSidepanelDateTitle}>Exp. date:</div>
          {document?.expirationDate
            ? formatDate(document?.expirationDate)
            : '-'}
        </div>
        <div className={cn.documentSidepanelDate}>
          <div className={cn.documentSidepanelDateTitle}>Loaded:</div>
          {formatDate(document?.createdAt as string, true)}
        </div>
      </div>
      {updateAllowed &&
        onChangeDocumentExpDate &&
        applicationStatus !== NewApplicationStatus.ACCEPTED &&
        applicationStatus !== NewApplicationStatus.DECLINED && (
          <div className={cn.changeExpDateWrapper}>
            <DateInput
              type={DateInputType.PSEUDO}
              placeholder={
                updateInProgress ? 'Loading...' : 'Change expiration date'
              }
              value={document?.expirationDate || ''}
              onChange={(date) =>
                onChangeDocumentExpDate &&
                onChangeDocumentExpDate(date.toISOString())
              }
              fullWidth
              withoutHidePlaceholder
              withoutCustomSelector
            />
          </div>
        )}
      {changeDateError && (
        <div className={cn.updateError}>{changeDateError}</div>
      )}
      {applicationStatus !== NewApplicationStatus.ACCEPTED &&
        applicationStatus !== NewApplicationStatus.DECLINED &&
        renameAvailable && (
          <div className={cn.documentSidepanelComments}>
            <div className={cn.documentSidepanelCommentsTitle}>
              Document Name
            </div>
            <InputText
              name="documentName"
              placeholder="Enter document name"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              disabled={!document}
              error={nameError}
            />
          </div>
        )}

      {applicationStatus !== NewApplicationStatus.ACCEPTED &&
        applicationStatus !== NewApplicationStatus.DECLINED &&
        commentsVisible && (
          <div className={cn.documentSidepanelComments}>
            <div className={cn.documentSidepanelCommentsTitle}>
              Add a comment
            </div>
            <Textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Enter a comment..."
              name="comment"
              className={cn.documentSidepanelCommentTextarea}
            />
            <ButtonSecondary
              className={`button-white ${cn.documentSidepanelSendComment}`}
              disabled={!comment || commentInProgress}
              onClick={() => {
                addCommentAction(comment);
                setComment('');
              }}
              text={commentInProgress ? 'Loading...' : 'Post'}
            />
          </div>
        )}

      {document?.commentsList && document.commentsList.length > 0 && (
        <div className={cn.documentSidepanelComments}>
          {document.commentsList.map((item) => (
            <DocumentComment
              key={item.id}
              comment={item}
              documentId={document.id}
              commentsVisible={commentsVisible}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Document;
